import { useState, useEffect, useRef, useCallback } from "react";
import { Clock, AlertTriangle, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TimerBar — Sticky countdown timer for timed TA/HOS sections
 * 
 * Props:
 *  - candidateId: string
 *  - sectionId: string
 *  - timeLimitSeconds: number
 *  - onTimeout: () => void — called when time expires
 *  - onTick: (remaining: number) => void — called each second (optional)
 *  - deferred: boolean — if true, timer is created but doesn't start until activated
 *  - activated: boolean — external signal that the timer should be activated (first response saved)
 *  - qaMode: boolean — if true, show timer in preview mode without starting
 */
export function TimerBar({ candidateId, sectionId, timeLimitSeconds, onTimeout, onTick, deferred = false, activated = false, qaMode = false }) {
  const [remaining, setRemaining] = useState(timeLimitSeconds);
  const [started, setStarted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const intervalRef = useRef(null);
  const heartbeatRef = useRef(null);
  const elapsedRef = useRef(0);
  const activatedRef = useRef(false);

  // Start or resume timer on mount
  useEffect(() => {
    if (!candidateId || !sectionId || !timeLimitSeconds || qaMode) return;

    const startTimer = async () => {
      try {
        const res = await fetch(`${API}/ta/timer/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidate_id: candidateId,
            section_id: sectionId,
            time_limit_seconds: timeLimitSeconds,
            deferred: deferred,
          }),
        });
        const data = await res.json();

        if (data.timed_out || data.completed) {
          setTimedOut(true);
          setRemaining(0);
          onTimeout?.();
          return;
        }

        if (data.deferred && !data.started_at) {
          // Timer created but not started — waiting for first response
          setWaitingForResponse(true);
          return;
        }

        // Timer already running (or non-deferred)
        const elapsed = data.elapsed_seconds || 0;
        elapsedRef.current = elapsed;
        setRemaining(Math.max(0, timeLimitSeconds - elapsed));
        setStarted(true);
        setWaitingForResponse(false);
      } catch (err) {
        console.error("Failed to start timer:", err);
        if (!deferred) setStarted(true);
        else setWaitingForResponse(true);
      }
    };

    startTimer();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [candidateId, sectionId, timeLimitSeconds, deferred, qaMode]);

  // Activate timer when first non-empty response is saved
  useEffect(() => {
    if (!activated || !waitingForResponse || activatedRef.current || qaMode) return;
    activatedRef.current = true;

    const activate = async () => {
      try {
        const res = await fetch(`${API}/ta/timer/activate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidate_id: candidateId,
            section_id: sectionId,
          }),
        });
        const data = await res.json();
        if (data.started_at) {
          setWaitingForResponse(false);
          setRemaining(timeLimitSeconds);
          elapsedRef.current = 0;
          setStarted(true);
        }
      } catch (err) {
        console.error("Failed to activate timer:", err);
        // Start locally as fallback
        setWaitingForResponse(false);
        setRemaining(timeLimitSeconds);
        elapsedRef.current = 0;
        setStarted(true);
      }
    };

    activate();
  }, [activated, waitingForResponse, candidateId, sectionId, timeLimitSeconds, qaMode]);

  // Countdown logic
  useEffect(() => {
    if (!started || timedOut) return;

    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;
        elapsedRef.current += 1;
        onTick?.(next);

        if (next <= 0) {
          clearInterval(intervalRef.current);
          setTimedOut(true);
          onTimeout?.();
          return 0;
        }
        return next;
      });
    }, 1000);

    // Heartbeat: sync elapsed time to backend every 15 seconds
    heartbeatRef.current = setInterval(async () => {
      try {
        await fetch(`${API}/ta/timer/heartbeat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidate_id: candidateId,
            section_id: sectionId,
            elapsed_seconds: elapsedRef.current,
          }),
        });
      } catch {
        // Ignore heartbeat failures
      }
    }, 15000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(heartbeatRef.current);
    };
  }, [started, timedOut, candidateId, sectionId]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // QA mode: show time limit without countdown
  if (qaMode) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="timer-bar-qa">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            QA Preview — {formatTime(timeLimitSeconds)} time limit (timer not running)
          </span>
        </div>
      </div>
    );
  }

  // Waiting for first response
  if (waitingForResponse) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="timer-bar-waiting">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-blue-700">
            {formatTime(timeLimitSeconds)} — Timer starts when you begin answering
          </span>
        </div>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div
        className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3"
        data-testid="timer-bar-expired"
      >
        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
        <span className="text-sm font-medium text-red-700">
          Time expired for this section. Your responses have been saved.
        </span>
      </div>
    );
  }

  const percentage = timeLimitSeconds > 0 ? (remaining / timeLimitSeconds) * 100 : 0;
  const isWarning = remaining <= 300 && remaining > 60; // < 5 min
  const isCritical = remaining <= 60; // < 1 min

  const barColor = isCritical
    ? "bg-red-500"
    : isWarning
    ? "bg-amber-500"
    : "bg-[#00AEEF]";

  const textColor = isCritical
    ? "text-red-600"
    : isWarning
    ? "text-amber-600"
    : "text-[#3b3b3b]";

  return (
    <div
      className="bg-white border rounded-lg px-4 py-2.5 shadow-sm"
      data-testid="timer-bar"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${textColor}`} />
          <span className={`text-sm font-medium ${textColor}`}>
            {formatTime(remaining)} remaining
          </span>
        </div>
        {(isWarning || isCritical) && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isCritical ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
          }`}>
            {isCritical ? "FINAL MINUTE" : "< 5 MIN"}
          </span>
        )}
      </div>
      {/* Progress bar */}
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
